/**
 * @React
 */
import React, { FC, ReactNode, useEffect, useState, useCallback, useRef } from "react";

import { useHistory } from "react-router-dom";
/**
 * @MUI
 */
import { Box, Button, IconButton, Grid, Table, TableBody, TableCell, TableHead, TableRow, Divider } from "@mui/material";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
/**
 * @Components
 */
import Loader from "../../Loader";
import CardFieldSet from "../../CustomComponents/CardFieldSet";
import { MonitoringLogsDetailsCgs } from "../../detailedApplication/MonitoringApplication";
import { IMonitoringCgsFields } from "../../../types/monitoringCgsFields";
import CustomField from "../../CustomComponents/CustomFields/input";
import AutoComplete from "../../CustomComponents/CustomFields/AutoComplete";
import FilesContainer from "../../CustomComponents/FilesContainer";
import FileUploadDropbox from "../../dialogs/FileUploadDropbox";
/**
 * @ReactHookForm
 */
import { Controller, FormProvider, SubmitErrorHandler, useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
/**
 * @Types
 */
import {
  IMonitoringFields,
  IInputField,
  IMonitoringAgreementModel,
  UploadedAttachments,
  IDifferenceFromAgreement,
  IMonitoringAgreementCgsModel,
  IInputFieldOption,
} from "../../../types";
/**
 * @Validation
 */
import { monitoringInitialDataSchema } from "../../../utilities/Fields/monitoringFields/fields/forms/validation";
/**
 * @Hooks
 */
import { useFormCalculations } from "../../../hooks/useMonitoringCgsHook";
import { useDependentOptions } from "../../../hooks/useDepenentOptionsMonitoring";
/**
 * @Utilities
 */
import { getFirstErrorMessage } from "../../../utilities/Fields/monitoringFields/utils";
import { monitoringCgsGroupFieldsInitValues } from "../../../utilities/Fields/CgsMonitoringFields/fields/forms";
import { PostData } from "../../../utilities/PostData";
import { generateUrl } from "../../../utilities/generateRequestUrl";
import axiosInstance from "../../../utilities/axios";
import config from "../../../utilities/generateRequestUrl/config";
import { monitoringCgsInitialDataSchema } from "../../../utilities/Fields/CgsMonitoringFields/fields/forms/validation";
import { fieldsCgs, valueFormatCgs } from "../../../utilities/Fields/CgsMonitoringFields/fields/changesFields";
import DetailTableTR from "../../CustomComponents/TablesCompoent/Detail/DetailTableTR";
import DetailePageStyles from "../../../utilities/DetailePageStayles";
import { fields } from "../../../utilities/Fields/monitoringFields/fields/changesFields";

interface MonitoringCgsFormProps {
  data: IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>;
  load: boolean;
  url: string;
  opts?: any;
  generatedValues?: any;
}
const MonitoringCgsForm: FC<MonitoringCgsFormProps> = ({ data, load, url, opts, generatedValues }) => {
  const history = useHistory();
  const classes = DetailePageStyles();
  const [submitReload, setSubmitReload] = useState<boolean>(false);
  const [uploadedAttachments, setUploadedAttachments] = useState<UploadedAttachments>({ monitoring_attachments: [] });
  const [projectActivity, setProjectActivity] = useState<{ label: string; value: string } | null>(null);

  const methods = useForm<IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>>({
    defaultValues: (data as IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>) || {},
    resolver: yupResolver(monitoringCgsInitialDataSchema) as any,
    mode: "onBlur",
  });
  const { setValue, getValues, control, handleSubmit, watch, formState } = methods as any;

  const { errors } = formState;

  const {
    fields: FilesFields,
    append: AppendFile,
    remove: RemoveFile,
  } = useFieldArray({
    control,
    name: "attached_documents.monitoring_attachments",
  });
  let {
    fields: projectActivityFields,
    append: appendProjectActivity,
    remove: removeProjectActivity,
  } = useFieldArray({
    control,
    name: "project_activities",
  });

  const watchedValues = watch();
  const { visibilityConditions } = useFormCalculations(watchedValues, setValue, methods.control._defaultValues);
  const { optionsArray } = useDependentOptions(watchedValues.actual_municipality, opts.mountainous_settlements_per_municipality);

  const shouldHideField = (fieldName: keyof typeof visibilityConditions) => {
    return visibilityConditions[fieldName] === false; // Return true if the field should be hidden
  };

  const submitFn = async (data: IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>) => {
    try {
      setSubmitReload(true);
      const resp = await axiosInstance.post(generateUrl(config.client, url, null, null), {
        application: data,
      });
      if (resp && resp.data && resp.statusText === "OK") {
        history.push("/monitoring");
      }
    } catch (e) {
      if (e) {
        alert("დაფიქსირდა შეცდომა");
      }
      console.error(e);
    } finally {
      setSubmitReload(false);
    }
  };

  const onError: SubmitErrorHandler<IMonitoringFields & Partial<IMonitoringAgreementModel>> = (errors) => {
    const message = getFirstErrorMessage(errors);
    alert(message);
  };

  const onSubmit = (data: IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>) => {
    const transformedData = { ...data };
    if (transformedData.attached_documents?.monitoring_attachments) {
      transformedData.attached_documents.monitoring_attachments = transformedData.attached_documents.monitoring_attachments.map((file: any) => {
        return typeof file === "string" ? file : file.original_path;
      });
    }
    submitFn(transformedData);
  };
  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <>
          {opts && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                width: `100%`,
              }}
            >
              <FormProvider {...(methods as any)}>
                {
                  <form onSubmit={handleSubmit(onSubmit, onError)} noValidate>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                        width: `100%`,
                      }}
                    >
                      {monitoringCgsGroupFieldsInitValues.map((group, groupIndex) => {
                        if (group.groupName === "fixed_assets_capital_expenditures_funds_turnover") {
                          return (
                            <CardFieldSet key={groupIndex} title={group.groupLabel}>
                              {group.fields.map((item: IInputField<IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>>, itemIndex) => {
                                if (shouldHideField(item.name as keyof typeof visibilityConditions)) {
                                  return null;
                                }
                                if (item.name === "amount_of_used_amount_table") {
                                  if (item.sub_fields) {
                                    const rows = [];
                                    for (let i = 0; i < item.sub_fields.length; i += 3) {
                                      rows.push(item.sub_fields.slice(i, i + 3));
                                    }

                                    return (
                                      <>
                                        <table className={classes.table}>
                                          <thead>
                                            <tr>
                                              <th style={{ fontSize: "12px", textAlign: "left", padding: "8px", width: "33.33%" }}>
                                                პროექტის ფარგლებში განხორციელებული ხარჯვა;
                                              </th>
                                              <th style={{ fontSize: "12px", textAlign: "left", padding: "8px", width: "33.33%" }}>მიზნობრივი ხარჯვა;</th>
                                              <th style={{ fontSize: "12px", textAlign: "left", padding: "8px", width: "33.33%" }}>
                                                არამიზნობრივი ხარჯვა;
                                                <i
                                                  style={{
                                                    fontSize: "10px",
                                                    fontWeight: 100,
                                                    color: "rgb(0, 0, 0, 0.8)",
                                                  }}
                                                >
                                                  (მოიცავს შემდეგს: არ არის შეძენილი გუდვილი; არ არის შეძენილი სატრანსპორტო საშუალებები და არ განხორციელებულა
                                                  მათი რემონტი, გარდა სპეციალური საშუალებებისა, რომლებიც უშუალოდ გამოიყენება საწარმოს ტერიტორიაზე
                                                  პროდუქციის/მომსახურების წარმოების პროცესში; სახელმწიფო ან/და კერძო საკუთრებაში არსებული უძრავი ქონების შეძენა
                                                  ან/და საქართველოს სამოქალაქო კოდექსით გათვალისწინებული ფორმებით დროებით სარგებლობაში აღება; კერძო სამართლის
                                                  იურიდიული პირის აქციების ან წილების შეძენა ან მართვაში აღება; საქართველოს კანონმდებლობის შესაბამისად
                                                  გათვალისწინებული სახდელების (ჯარიმა, საურავი, პირგასამტეხლო და სხვა) გადახდა/დაფარვა)
                                                </i>{" "}
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {rows.map((row, index) => (
                                              <tr
                                                key={`${groupIndex}-${itemIndex}-${index}`}
                                                style={{
                                                  borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                                  borderTop: "1px solid rgb(0, 0, 0, 0.23)",
                                                }}
                                              >
                                                <td
                                                  key={`${groupIndex}-${itemIndex}-${index}-0`}
                                                  style={{
                                                    padding: "4px",
                                                  }}
                                                >
                                                  {row[0].disabled ? (
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "space-between",
                                                        minHeight: "100px",
                                                      }}
                                                    >
                                                      <label
                                                        style={{
                                                          fontSize: "11px",
                                                          padding: "8px",
                                                          color: "rgb(0, 0, 0, 0.54)",
                                                        }}
                                                      >
                                                        {row[0].label}
                                                      </label>
                                                      <Controller
                                                        key={row[0].name}
                                                        name={row[0].name}
                                                        control={control}
                                                        render={({ field }) => (
                                                          <output
                                                            style={{
                                                              fontSize: "11px",
                                                              padding: "8px",
                                                              height: "20px",
                                                            }}
                                                          >
                                                            {field.value || 0}
                                                          </output>
                                                        )}
                                                      />
                                                    </Box>
                                                  ) : (
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "space-between",
                                                        minHeight: "100px",
                                                      }}
                                                    >
                                                      <label
                                                        style={{
                                                          fontSize: "11px",
                                                          padding: "8px",
                                                          color: "rgb(0, 0, 0, 0.54)",
                                                        }}
                                                      >
                                                        {row[0].label}
                                                      </label>

                                                      <CustomField
                                                        name={row[0].name}
                                                        label={""}
                                                        type={
                                                          (row[0].type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date") ||
                                                          "text"
                                                        }
                                                        control={control}
                                                        disabled={row[0].disabled}
                                                        errors={errors[row[0].name]}
                                                      />
                                                    </Box>
                                                  )}
                                                </td>
                                                <td
                                                  key={`${groupIndex}-${itemIndex}-${index}-1`}
                                                  style={{
                                                    padding: "4px",
                                                  }}
                                                >
                                                  {row[1].disabled ? (
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "space-between",
                                                        minHeight: "100px",
                                                      }}
                                                    >
                                                      <label
                                                        style={{
                                                          fontSize: "11px",
                                                          padding: "8px",
                                                          color: "rgb(0, 0, 0, 0.54)",
                                                        }}
                                                      >
                                                        {row[1].label}
                                                      </label>
                                                      <Controller
                                                        key={row[1].name}
                                                        name={row[1].name}
                                                        control={control}
                                                        render={({ field }) => (
                                                          <output
                                                            style={{
                                                              fontSize: "11px",
                                                              padding: "8px",
                                                              height: "20px",
                                                            }}
                                                          >
                                                            {field.value || 0}
                                                          </output>
                                                        )}
                                                      />
                                                    </Box>
                                                  ) : (
                                                    <Box
                                                      sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        justifyContent: "space-between",
                                                        minHeight: "100px", // Ensures enough height
                                                      }}
                                                    >
                                                      <label
                                                        style={{
                                                          fontSize: "11px",
                                                          padding: "8px",
                                                          color: "rgb(0, 0, 0, 0.54)",
                                                        }}
                                                      >
                                                        {row[1].label}
                                                      </label>

                                                      <CustomField
                                                        name={row[1].name}
                                                        label={""}
                                                        type={
                                                          (row[1].type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date") ||
                                                          "text"
                                                        }
                                                        control={control}
                                                        disabled={row[1].disabled}
                                                        errors={errors[row[1].name]}
                                                      />
                                                    </Box>
                                                  )}
                                                </td>
                                                <td
                                                  key={`${groupIndex}-${itemIndex}-${index}-2`}
                                                  style={{
                                                    padding: "4px",
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      justifyContent: "space-between",
                                                      minHeight: "100px",
                                                    }}
                                                  >
                                                    <label
                                                      style={{
                                                        fontSize: "11px",
                                                        padding: "8px",
                                                        color: "rgb(0, 0, 0, 0.54)",
                                                      }}
                                                    >
                                                      {row[2].label}
                                                    </label>
                                                    <Controller
                                                      key={row[2].name}
                                                      name={row[2].name}
                                                      control={control}
                                                      render={({ field }) => (
                                                        <output
                                                          style={{
                                                            fontSize: "11px",
                                                            padding: "8px",
                                                            height: "20px",
                                                          }}
                                                        >
                                                          {field.value || 0}
                                                        </output>
                                                      )}
                                                    />
                                                  </Box>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </>
                                    );
                                  }
                                }
                                if (item.name === "project_activities") {
                                  const project_activities = opts[item.optionName ?? "cgs_nace_activities"] as { value: string; label: string }[];
                                  const projectActivitiesValues = generatedValues[item.optionName ?? "cgs_nace_activities"];
                                  const pr = projectActivityFields.map((projectActivity, index) => {
                                    return (
                                      <>
                                        <Controller
                                          key={projectActivity.id}
                                          name={`project_activities.${index}`}
                                          control={control}
                                          render={({ field }) => {
                                            const projectActivityData = Object.values(project_activities).find(
                                              (activity: { label: string; value: string }) => activity?.value === field.value // Find the activity by id
                                            );
                                            return (
                                              <Box
                                                sx={{
                                                  padding: "0 15px 0 50px",
                                                  width: "100%",
                                                }}
                                              >
                                                <table className={classes.table}>
                                                  <tbody>
                                                    <>
                                                      <DetailTableTR
                                                        label={projectActivityData ? `${projectActivityData?.label}` : `აქტივობა არ მოიძებნა (${field.value})`}
                                                        loading={false}
                                                      >
                                                        <IconButton
                                                          sx={{
                                                            color: "rgb(211, 47, 47)",
                                                          }}
                                                          onClick={() => {
                                                            removeProjectActivity(index);
                                                            setProjectActivity(projectActivityData || null);
                                                          }}
                                                        >
                                                          <RemoveOutlinedIcon
                                                            sx={{
                                                              fontSize: "12px",
                                                              color: "rgb(211, 47, 47)",
                                                            }}
                                                          />
                                                        </IconButton>
                                                      </DetailTableTR>
                                                      {projectActivityData &&
                                                        projectActivitiesValues[projectActivityData?.value]["program_compatibility"] === false ? (
                                                        <tr>
                                                          <td
                                                            style={{
                                                              padding: "4px",
                                                              width: "100%",
                                                              color: "rgb(211, 47, 47)",
                                                              fontSize: "11px",
                                                            }}
                                                          >
                                                            არ ჯდება პროგრამაში
                                                          </td>
                                                        </tr>
                                                      ) : null}
                                                    </>
                                                  </tbody>
                                                </table>
                                              </Box>
                                            );
                                          }}
                                        />
                                      </>
                                    );
                                  });
                                  const autoCompleteComponent = () => (
                                    <>
                                      <Grid
                                        container
                                        sx={{
                                          width: "100%",
                                          justifyContent: "center",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Grid item xs={11}>
                                          {project_activities && (
                                            <AutoComplete
                                              name={"activity"}
                                              value={projectActivity}
                                              label={item.label}
                                              options={Object.values(project_activities) as { value: string; label: string }[]}
                                              callback={(data) => {
                                                if (data) setProjectActivity(data);
                                              }}
                                            />
                                          )}
                                        </Grid>
                                        <Grid item xs={1} display="flex" justifyContent="center">
                                          <IconButton
                                            disabled={projectActivity ? false : true}
                                            onClick={() => {
                                              appendProjectActivity(projectActivity?.value);
                                              setProjectActivity(null);
                                            }}
                                            sx={{
                                              cursor: "pointer",
                                            }}
                                          >
                                            <AddOutlinedIcon
                                              sx={{
                                                fontSize: "12px",
                                                color: !!projectActivity ? "rgb(44, 91, 148, 0.8)" : "rgb(0,0,0,0.23)",
                                              }}
                                            />
                                          </IconButton>
                                        </Grid>
                                      </Grid>
                                      {pr}
                                    </>
                                  );
                                  return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "left",
                                        gap: "20px",
                                      }}
                                    >
                                      {autoCompleteComponent()}
                                    </Box>
                                  );
                                }
                                if (item.name === "monitoring_new_deadline") {
                                  return (
                                    <>
                                      <Controller
                                        key={"monitoring_new_deadline"}
                                        name={"monitoring_new_deadline"}
                                        control={control}
                                        render={({ field }) => {
                                          return (
                                            <Box
                                              sx={{
                                                padding: "12px",
                                                width: "100%",
                                              }}
                                            >
                                              <DetailTableTR label={item.label} loading={false}>
                                                <>{field.value ? valueFormatCgs[field.name as keyof typeof valueFormatCgs](field.value) : ""}</>
                                              </DetailTableTR>
                                            </Box>
                                          );
                                        }}
                                      />
                                    </>
                                  );
                                }
                                if (item.name === "is_taken_business_loan_leasing") {
                                  return (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "left",
                                        gap: "20px",
                                      }}
                                    >
                                      <CustomField
                                        name={item.name}
                                        label={item.label}
                                        value={item.value}
                                        type={(item.type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date") || "text"}
                                        control={control}
                                        options={[
                                          { value: true, label: "კი" },
                                          { value: false, label: "არა" },
                                        ]}
                                        // disabled={item.disabled}
                                        errors={errors[item.name]}
                                      />
                                    </Box>
                                  );
                                }
                                return (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "left",
                                      gap: "20px",
                                    }}
                                  >
                                    <CustomField
                                      name={item.name}
                                      label={item.label}
                                      type={(item.type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date") || "text"}
                                      control={control}
                                      {...(item.optionName && opts
                                        ? { options: Object.values(opts[item.optionName]) }
                                        : item.options
                                          ? { options: item.options }
                                          : {})}
                                      disabled={item.disabled}
                                      errors={errors[item.name]}
                                    />
                                  </Box>
                                );
                              })}
                            </CardFieldSet>
                          );
                        }
                        return (
                          <CardFieldSet key={groupIndex} title={group.groupLabel}>
                            <>
                              {group.fields.map((item: IInputField<IMonitoringCgsFields & Partial<IMonitoringAgreementCgsModel>>) => {
                                if (shouldHideField(item.name as keyof typeof visibilityConditions)) {
                                  return null;
                                }
                                if (item.name === "mountainous_settlement") {
                                  return (
                                    <CustomField
                                      name={item.name}
                                      label={item.label}
                                      type={item.type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date" | "day"}
                                      control={control}
                                      options={optionsArray}
                                      disabled={item.disabled}
                                      errors={errors[item.name]}
                                    />
                                  );
                                }
                                return (
                                  <CustomField
                                    name={item.name}
                                    label={item.label}
                                    type={item.type as "text" | "select" | "textarea" | "checkbox" | "autocomplete" | "number" | "date" | "day"}
                                    control={control}
                                    {...(item.optionName && opts
                                      ? { options: Object.values(opts[item.optionName]) }
                                      : item.options
                                        ? { options: item.options }
                                        : {})}
                                    disabled={item.disabled}
                                    errors={errors[item.name]}
                                  />
                                );
                              })}
                            </>
                          </CardFieldSet>
                        );
                      })}
                      {
                        <CardFieldSet key={`100000001`} title={"ასატვირთი დოკუმენტები"}>
                          <>
                            <FileUploadDropbox
                              uploadUrl="/bank/application/upload_attachments/monitoring-cgs"
                              onUploadSuccess={(data) => {
                                if (data.monitoring_attachments) {
                                  data.monitoring_attachments.forEach((fileData: any) => {
                                    AppendFile(fileData);
                                  });
                                }
                              }}
                              fieldName="monitoring_attachments"
                              multiple={true}
                            />
                            <Controller
                              name="attached_documents.monitoring_attachments"
                              control={control}
                              render={() => (
                                <FilesContainer
                                  FilesList={methods.getValues("attached_documents.monitoring_attachments")}
                                  mode="form"
                                  removeCallback={(i) => {
                                    RemoveFile(i);
                                  }}
                                  cols={6}
                                />
                              )}
                            />
                          </>
                        </CardFieldSet>
                      }
                      {data && data?.difference_from_agreement && data?.difference_from_agreement.length > 0 && (
                        <CardFieldSet key={`10000002`} title={"განაცხადის ცვლილების ისტორია"}>
                          <>
                            <MonitoringLogsDetailsCgs
                              changedDataHistory={data?.difference_from_agreement ?? ([] as IDifferenceFromAgreement[])}
                              valueFormat={valueFormatCgs}
                              fields={fieldsCgs}
                            />
                          </>
                        </CardFieldSet>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          width: `100%`,
                        }}
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          sx={{
                            width: "100%",
                          }}
                        >
                          გაგზავნა
                        </Button>
                      </Box>
                    </Box>
                  </form>
                }
              </FormProvider>
            </Box>
          )}
        </>
      )}
    </>
  );
};

export default MonitoringCgsForm;

/**
 *                                                                           <>
                                            {
                                              if(item.name === "amount_of_used_amount_table"){
                                                return(<>hh</>)
                                              }
                                                
                                            } 
                                            
                                            </>

                                          // if(){
                                            
                                          // }
                                            // <tr style={{
                                            //   borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                            //   padding: "12px",
                                            // }}>
                                            //   <td
                                            //     colSpan={3}
                                            //     style={{
                                            //       padding: "20px",
                                            //     }}
                                            //   >
                                            //     <Box
                                            //       sx={{
                                            //         display: "flex",
                                            //         flexDirection: "column",
                                            //         justifyContent: "left",
                                            //         gap: "20px",
                                            //       }}
                                            //     >
                                                  
                                            //     </Box>
                                            //   </td>

                                            // </tr>)

                                          // <tr style={{
                                          //   borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                          //   padding: "12px",
                                          // }}>
                                          //   <td
                                          //     colSpan={3}
                                          //     style={{
                                          //       padding: "20px",
                                          //     }}
                                          //   >
                                          //     <Box
                                          //       sx={{
                                          //         display: "flex",
                                          //         flexDirection: "column",
                                          //         justifyContent: "left",
                                          //         gap: "20px",
                                          //       }}
                                          //     ></Box>
                                          //     <td>

                                          //     </tr>
                                          // return (
                                          //   <CustomField
                                          //     name={item.name}
                                          //     label={item.label}
                                          //     type={item.type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' | 'day'}
                                          //     control={control}
                                          //     {...(item.optionName && opts ? { options: Object.values(opts[item.optionName]) } : item.options ? { options: item.options } : {})}
                                          //     disabled={item.disabled}
                                          //     errors={errors[item.name]}
                                          //   />
                                          // );
 */

/**
 * 
 *                                 <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                  }}
                                >
                                  <thead>
                                    <tr>
                                      <th style={{ textAlign: "left", padding: "8px", width: "33.33%" }}>პროექტის ფარგლებში განხორციელებული ხარჯვა;</th>
                                      <th style={{ textAlign: "left", padding: "8px", width: "33.33%" }}>მიზნობრივი ხარჯვა;</th>
                                      <th style={{ textAlign: "left", padding: "8px", width: "33.33%" }}>არამიზნობრივი ხარჯვა;<i style={{
                                        fontSize: "10px",
                                        fontWeight: 100,
                                        color: "rgb(0, 0, 0, 0.54)",
                                      }}>(მოიცავს შემდეგს: არ არის შეძენილი გუდვილი; არ არის შეძენილი სატრანსპორტო საშუალებები და არ განხორციელებულა მათი რემონტი, გარდა სპეციალური საშუალებებისა, რომლებიც უშუალოდ გამოიყენება საწარმოს ტერიტორიაზე პროდუქციის/მომსახურების წარმოების პროცესში; სახელმწიფო ან/და კერძო საკუთრებაში არსებული უძრავი ქონების შეძენა ან/და საქართველოს სამოქალაქო კოდექსით გათვალისწინებული ფორმებით დროებით სარგებლობაში აღება; კერძო სამართლის იურიდიული პირის აქციების ან წილების შეძენა ან მართვაში აღება; საქართველოს კანონმდებლობის შესაბამისად გათვალისწინებული სახდელების (ჯარიმა, საურავი, პირგასამტეხლო და სხვა) გადახდა/დაფარვა)</i> </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr key={'10000000001'}
                                      style={{
                                        borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                        borderTop: "1px solid rgb(0, 0, 0, 0.23)",
                                      }}
                                    >
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px", // Ensures enough height
                                          }}
                                        >
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>{group.fields[0].label}</label>

                                          <CustomField
                                            name={group.fields[0].name}
                                            label={''}
                                            type={group.fields[0].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[0].disabled}
                                            errors={errors[group.fields[0].name]}
                                          />
                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px", // Ensures enough height
                                          }}
                                        >
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>{group.fields[1].label}</label>
                                          <CustomField
                                            name={group.fields[1].name}
                                            label={''}
                                            type={group.fields[1].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[1].disabled}
                                            errors={errors[group.fields[1].name]}
                                          />

                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>
                                            {group.fields[2].label}
                                          </label>
                                          <Controller
                                            key={group.fields[2].name}
                                            name={group.fields[2].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                    </tr>
                                    <tr key={'10000002'}
                                      style={{
                                        borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                      }}
                                    >
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px", // Ensures enough height
                                          }}
                                        >
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>{group.fields[3].label}</label>

                                          <CustomField
                                            name={group.fields[3].name}
                                            label={''}
                                            type={group.fields[3].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[3].disabled}
                                            errors={errors[group.fields[3].name]}
                                          />
                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px", // Ensures enough height
                                          }}
                                        >
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>{group.fields[4].label}</label>
                                          <CustomField
                                            name={group.fields[4].name}
                                            label={''}
                                            type={group.fields[4].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[4].disabled}
                                            errors={errors[group.fields[4].name]}
                                          />

                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>
                                            {group.fields[5].label}
                                          </label>
                                          <Controller
                                            key={group.fields[5].name}
                                            name={group.fields[5].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                    </tr>
                                    <tr key={'10000003'}
                                      style={{
                                        borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                      }}
                                    >
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px", // Ensures enough height
                                          }}
                                        >
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>{group.fields[6].label}</label>

                                          <CustomField
                                            name={group.fields[6].name}
                                            label={''}
                                            type={group.fields[6].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[6].disabled}
                                            errors={errors[group.fields[6].name]}
                                          />
                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px", // Ensures enough height
                                          }}
                                        >
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>{group.fields[7].label}</label>
                                          <CustomField
                                            name={group.fields[7].name}
                                            label={''}
                                            type={group.fields[7].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[7].disabled}
                                            errors={errors[group.fields[7].name]}
                                          />

                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>
                                            {group.fields[8].label}
                                          </label>
                                          <Controller
                                            key={group.fields[8].name}
                                            name={group.fields[8].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                    </tr>
                                    <tr key={'10000004'}
                                      style={{
                                        borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                      }}
                                    >
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px", // Ensures enough height
                                          }}
                                        >
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>{group.fields[9].label}</label>

                                          <CustomField
                                            name={group.fields[9].name}
                                            label={''}
                                            type={group.fields[9].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[9].disabled}
                                            errors={errors[group.fields[9].name]}
                                          />
                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px", // Ensures enough height
                                          }}
                                        >
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>{group.fields[10].label}</label>
                                          <CustomField
                                            name={group.fields[10].name}
                                            label={''}
                                            type={group.fields[10].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[10].disabled}
                                            errors={errors[group.fields[10].name]}
                                          />

                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label
                                            style={{
                                              fontSize: "11px",
                                              padding: "8px",
                                              color: "rgb(0, 0, 0, 0.54)",
                                            }}>
                                            {group.fields[11].label}
                                          </label>
                                          <Controller
                                            key={group.fields[11].name}
                                            name={group.fields[11].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                    </tr>
                                    <tr key={'10000006'}
                                      style={{
                                        borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                      }}
                                    >
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>
                                            {group.fields[12].label}
                                          </label>
                                          <Controller
                                            key={group.fields[12].name}
                                            name={group.fields[12].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>
                                            {group.fields[13].label}
                                          </label>
                                          <Controller
                                            key={group.fields[13].name}
                                            name={group.fields[13].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>
                                            {group.fields[14].label}
                                          </label>
                                          <Controller
                                            key={group.fields[14].name}
                                            name={group.fields[14].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                    </tr>
                                    <tr key={'10000005'}
                                      style={{
                                        borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                      }}
                                    >
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px", // Ensures enough height
                                          }}
                                        >
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>{group.fields[15].label}</label>

                                          <CustomField
                                            name={group.fields[15].name}
                                            label={''}
                                            type={group.fields[15].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[15].disabled}
                                            errors={errors[group.fields[15].name]}
                                          />
                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px", // Ensures enough height
                                          }}
                                        >
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>{group.fields[16].label}</label>
                                          <CustomField
                                            name={group.fields[16].name}
                                            label={''}
                                            type={group.fields[16].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[16].disabled}
                                            errors={errors[group.fields[16].name]}
                                          />

                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>
                                            {group.fields[17].label}
                                          </label>
                                          <Controller
                                            key={group.fields[17].name}
                                            name={group.fields[17].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                    </tr>
                                    <tr key={'10000007'}
                                      style={{
                                        borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                      }}
                                    >
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>
                                            {group.fields[18].label}
                                          </label>
                                          <Controller
                                            key={group.fields[18].name}
                                            name={group.fields[18].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>
                                            {group.fields[19].label}
                                          </label>
                                          <Controller
                                            key={group.fields[19].name}
                                            name={group.fields[19].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                      <td style={{
                                        padding: "4px",
                                      }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            minHeight: "100px",
                                          }}>
                                          <label style={{
                                            fontSize: "11px",
                                            padding: "8px",
                                            color: "rgb(0, 0, 0, 0.54)",
                                          }}>
                                            {group.fields[20].label}
                                          </label>
                                          <Controller
                                            key={group.fields[20].name}
                                            name={group.fields[20].name}
                                            control={control}
                                            render={({ field }) => (
                                              <output
                                                style={{
                                                  fontSize: "11px",
                                                  padding: "8px",
                                                  height: "20px",
                                                }}
                                              >
                                                {field.value || 0}
                                              </output>
                                            )}
                                          />
                                        </Box>
                                      </td>
                                    </tr>
                                    <tr style={{
                                      borderBottom: "1px solid rgb(0, 0, 0, 0.23)",
                                      padding: "12px",
                                    }}>
                                      <td
                                        colSpan={3}
                                        style={{
                                          padding: "20px",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "left",
                                            gap: "20px",
                                          }}
                                        >
                                          {shouldHideField(group.fields[21].name as keyof typeof visibilityConditions) ? <>
                                            <CustomField
                                              name={group.fields[21].name}
                                              label={group.fields[21].label}
                                              type={group.fields[21].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' | 'text'}
                                              control={control}
                                              disabled={group.fields[21].disabled}
                                              errors={errors[group.fields[21].name]}
                                            />
                                          </> : null}
                                          {shouldHideField(group.fields[22].name as keyof typeof visibilityConditions) ? <>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "space-between",
                                                minHeight: "80px",
                                              }}
                                            >
                                              <label style={{
                                                fontSize: "11px",
                                                padding: "8px",
                                                color: "rgb(0, 0, 0, 0.54)",
                                              }}>{group.fields[22].label}</label>
                                              <CustomField
                                                name={group.fields[22].name}
                                                label={group.fields[22].label}
                                                type={group.fields[22].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' | 'text'}
                                                control={control}
                                                disabled={group.fields[22].disabled}
                                                errors={errors[group.fields[22].name]}
                                              />
                                            </Box>
                                          </> : null}

                                          {shouldHideField(group.fields[23].name as keyof typeof visibilityConditions) ? <>
                                            < CustomField
                                              name={group.fields[23].name}
                                              label={group.fields[23].label}
                                              type={group.fields[23].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' | 'text'}
                                              control={control}
                                              disabled={group.fields[23].disabled}
                                              errors={errors[group.fields[23].name]}
                                            />
                                          </> : null}


                                          {shouldHideField(group.fields[24].name as keyof typeof visibilityConditions) ? <CustomField
                                            name={group.fields[24].name}
                                            label={group.fields[24].label}
                                            type={group.fields[24].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[24].disabled}
                                            errors={errors[group.fields[24].name]}
                                          /> : null}
                                          <Divider />
                                          {
                                            autoCompleteComponent()
                                          }

                                          <CustomField
                                            name={group.fields[26].name}
                                            label={group.fields[26].label}
                                            type={group.fields[26].type as 'text' | 'select' | 'textarea' | 'checkbox' | 'autocomplete' | 'number' | 'date' || 'text'}
                                            control={control}
                                            disabled={group.fields[26].disabled}
                                            errors={errors[group.fields[26].name]}
                                          />
                                        </Box>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table >
 */
